import { DocumentReference } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";

export type DocumentReferenceRowData = {
  id: string;
  fileName: string; // Hidden field like in V1
  description: string;
  size: string;
  fileType: string;
  organization: string;
  date: string;
  originalData: Smart<DocumentReference>;
};

export function getDocumentReferenceRows({
  bundle,
}: {
  bundle: FhirBundleSdk;
}): DocumentReferenceRowData[] {
  try {
    const documentReferences = bundle.getDocumentReferences();
    return documentReferences.map(doc => {
      // Extract file name from content first attachment (hidden field)
      const attachment = doc.content?.[0]?.attachment;
      const fileName =
        attachment?.title || attachment?.url?.split("/").pop() || doc.type?.text || "-";

      // Extract description
      const description = doc.description || "-";

      // Extract size (improved formatting like V1)
      const size = getDocumentSize(doc);

      // Extract file type from content type
      const fileType = attachment?.contentType || "-";

      // Extract organization (from contained resources like V1)
      const organization = getOrganizationName(doc);

      // Extract date
      const date = doc.date ? dayjs(doc.date).format(ISO_DATE) : "-";

      return {
        id: doc.id || "-",
        fileName,
        description: description,
        size,
        fileType,
        organization,
        date,
        originalData: doc,
      };
    });
  } catch (error) {
    console.warn("DocumentReference not available in FhirBundleSdk:", error);
    return [];
  }
}

function getDocumentSize(doc: DocumentReference): string {
  if (doc.content?.[0]?.attachment?.size) {
    const sizeInBytes = doc.content[0].attachment.size;
    if (sizeInBytes) {
      return bytesToSize(sizeInBytes);
    }
  }
  return "-";
}

function bytesToSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function getOrganizationName(doc: DocumentReference): string {
  // Check contained resources first (like V1)
  if (doc.contained) {
    const org = doc.contained.find(c => c.resourceType === "Organization");
    if (org && "name" in org) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (org as any).name || "-";
    }
  }

  // Fallback to author
  if (doc.author?.[0]?.display) {
    return doc.author[0].display;
  }

  return "-";
}
